import { MapViewerNode, Viewer3dRelatedPreviewObject } from '@3ddv/dvm-internal';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, computed, effect, EffectRef, ElementRef, inject, OnDestroy, OnInit, Signal, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DvmFormatterPipe } from '@helpers/pipes/dvm-formatter.pipe';
import { AppNodes } from '@models/enums/dvm/DvmNodes.enum';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { NeighborComponent } from '@models/interfaces/components/Neighbor.interface';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { GeneralAdmission } from '@models/types/components/GeneralAdmission.type';
import { Neighbor, NeighborRecord } from '@models/types/components/Neighbor.type';
import { Translation } from '@models/types/components/Translation.type';
import { AmenitiesService } from '@services/amenities.service';
import { RelatedPreviewsService } from '@services/components/related-previews.service';
import { DvmService } from '@services/dvm.service';
import { FilterService } from '@services/filter.service';
import { TranslationService } from '@services/translation.service';
import { UiService } from '@services/ui.service';
import anime, { AnimeInstance } from 'animejs';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-neighbors',
  standalone: true,
  templateUrl: './neighbors.component.html',
  styleUrl: './neighbors.component.css',
  imports: [
    CommonModule,
    DvmFormatterPipe
  ],
  providers: [DvmFormatterPipe]
})
export class NeighborsComponent implements NeighborComponent, OnInit, OnDestroy  {

  // VIEWCHILD
  @ViewChild('neighborsContainer')
  public readonly  neighborsContainer!:   ElementRef<HTMLDivElement>;

  @ViewChild('currentSection', { static: false })
  public readonly  currentSection!:       ElementRef<HTMLSpanElement>;
  
  @ViewChild('currentSection3d', { static: false })
  public readonly  currentSection3d!:     ElementRef<HTMLSpanElement>;

  @ViewChild('leftSectionNumber', { static: false })
  public readonly  leftSectionElement!:   ElementRef<HTMLSpanElement>;

  @ViewChild('rightSectionNumber', { static: false })
  public readonly  rightSectionElement!:  ElementRef<HTMLSpanElement>;


  // SERVICES
  private readonly uiconfig:                Signal<UiConfiguration> = inject(UiService).uiConfig;
  private readonly dvmService:              DvmService              = inject(DvmService);
  private readonly amsService:              AmenitiesService        = inject(AmenitiesService);
  private readonly filterService:           FilterService           = inject(FilterService);
  private readonly relatedPreviewsService:  RelatedPreviewsService  = inject(RelatedPreviewsService);
  private readonly cdr:                     ChangeDetectorRef       = inject(ChangeDetectorRef);
  private readonly dvmFormatter:            DvmFormatterPipe        = inject(DvmFormatterPipe);
  private readonly route:                   ActivatedRoute          = inject(ActivatedRoute);
  public  readonly translation:             TranslationService      = inject(TranslationService);


  // CONFIGURATION
  private readonly config:                Signal<Neighbor>         = computed((): Neighbor => this.uiconfig()['Neighbors']);
  public  readonly data:                  Signal<Neighbor['data']> = computed((): Neighbor['data'] => this.config().data);
  public  readonly enabled:               Signal<boolean>          = computed((): boolean  => this.config().enabled);
  public  readonly modeLr:                Signal<boolean>          = computed((): boolean  => this.config().modeLr);
  public  readonly gaExceptions:          Signal<GeneralAdmission> = computed((): GeneralAdmission=> this.uiconfig().GeneralAdmission);
  public  readonly seatmapDisplayEnabled: Signal<boolean>          = computed((): boolean => this.uiconfig().SeatmapDisplay.enabled);
    
  
  
  //TRANSLATION CONFIG
  private readonly translationConfig:     Signal<Translation>         = computed((): Translation => this.uiconfig()['Translation']);
  public  readonly translationData:       Signal<Translation['data']> = computed((): Translation['data'] => this.translationConfig().data);
  public  readonly translationEnabled:    Signal<boolean>             = computed((): boolean  => this.translationConfig().enabled);


  // GLOBAL STATE
  private readonly   view:                Signal<AppViews|null>    = this.dvmService.view;
  protected readonly viewId:              string | null            = atob(this.route.snapshot.queryParamMap.get('view_id') || '');
  private   readonly relatedPreviewLabel: string | null            = atob(this.route.snapshot.queryParamMap.get('related_preview') || '');



  // COMPONENT STATE
  protected isLoading3d:                  boolean                                     = false;
  protected showElement:                  boolean                                     = false;
  private readonly subscriptions:         Subscription[]                              = [];
  protected readonly currentPreview:      Signal<Viewer3dRelatedPreviewObject | null> = this.relatedPreviewsService.currentPreview;
  protected initialViewId:                string | null                               = null;
  protected toggleAnimation:              AnimeInstance | null                        = null;


  // SECTION DATA
  protected selectedSection:              string | null            = null;
  protected sectionData:                  NeighborRecord | null    = null;

  // EFFECTS
  private onViewChange:                   EffectRef                = effect((): void =>{

    this.initialViewId = null;
    const view: AppViews | null = this.view();
    
    switch(view){
      
      // Seteamos sección, si está oculto mostramos elemento y comprobamos si el texto seatmap es false para toglearlo.
      case AppViews.Seatmap:
        
        this.setCurrentSection();
        
        if(!this.showElement || this.toggleAnimation) this.toggleNeighbors(true);
        
        break;
        
        // Seteamos sección en base al viewer3d, si está oculto mostramos elemento y comprobamos si el texto 3d es false para toglearlo.
        case AppViews.Pano:
        
        this.setCurrentSection(this.dvmService.viewer3d.getViewId());

        if(!this.showElement || this.toggleAnimation) this.toggleNeighbors(true);
        
        this.initialViewId = this.dvmService.viewer3d.getViewId();

        this.setCurrentPreview();

        
      break;

      // 
      default:

        if(this.showElement || this.toggleAnimation){

          this.toggleNeighbors(false);

          setTimeout((): void => this.setCurrentSection(null), 300);

        }

      break;

    }

  },{ allowSignalWrites: true });

  // GETTERS
  public get leftSection(): string | null {
    if (!this.sectionData || this.isGaException() || this.isGaException(this.sectionData.l)) return null;

    // If we are navigating with filters, check if the section on the left is inside the filter
    if(this.filterService.hasFilters && !this.filterService.getFilterByKey(AppNodes.Section).includes(this.sectionData.l) || !this.seatmapDisplayEnabled()) return null;

    const translation = this.translationEnabled() && this.translationData()[this.sectionData.l]
      ? this.translationData()[this.sectionData.l]
      : this.sectionData.l;

    return translation.length > 20 ? translation.substring(0, 20) + '...' : translation;
  }


  public get rightSection(): string | null {
    if (!this.sectionData || this.isGaException() || this.isGaException(this.sectionData.r)) return null;

    // If we are navigating with filters, check if the section on the right is inside the filter
    if((this.filterService.hasFilters && !this.filterService.getFilterByKey(AppNodes.Section).includes(this.sectionData.r)) || !this.seatmapDisplayEnabled()) return null;   

    const translation = this.translationEnabled() && this.translationData()[this.sectionData.r]
      ? this.translationData()[this.sectionData.r]
      : this.sectionData.r;

    return translation.length > 20 ? translation.substring(0, 20) + '...' : translation;
  }

  public get is3d(): boolean {

    return this.view() === AppViews.Pano;

  }

  public get sectionIsPremium(): boolean {

    const hasAmenities:    boolean = this.amsService.hasSelectedAmenities(),
          hasPremiumSeats: boolean = this.dvmService.viewer.getNodes().some(node => node.groups.includes('premium'));

    return hasAmenities || hasPremiumSeats;

  }

  public isGaException(viewId?: string): boolean {
    const sectionId = viewId ?? this.dvmService.viewer3d.getViewId();
    return !!sectionId && this.gaExceptions().enabled && this.gaExceptions().data.includes(sectionId);
  }


  ngOnInit(): void {
    this.initComponent();
  }

  ngOnDestroy(): void {

    this.subscriptions.forEach((sub: Subscription): void => sub.unsubscribe());
    this.onViewChange.destroy();

  }

  // METHODS
  public navigateToSection(section: MapViewerNode['id']): void {
    
    if(section === 'none' || !section) return; // Si no hay sección, no hacemos nada

    this.dvmService.loadMap(section).subscribe((): void => {
      this.setCurrentSection(section);
      this.amsService.checkSectionAmenity(this.dvmService.viewer.getNodeById(this.dvmService.currentMap!)!.original_id);
    });
    
  }

  private setCurrentSection(sectionId?: MapViewerNode['id'] | null): void {
    let section!: string | null;
    /**
     * El parametro sectionId puede ser null o undefined, si no es null, comprobamos
     * si es undefined. Si es undefined, tomamos el id del mapa actual, si no, lo tomamos del parametro.
     */
    if (sectionId !== null) {
      section =
        sectionId !== undefined ? sectionId : this.dvmService.currentMap;
    }

    const sectionData = section ? this.data()[section] : null;

    // Si ya hay, animamos el cambio de sección
    if (this.selectedSection && section) {
      
      this.animateSectionNumbers(section, sectionData!);
    }
    // Si no hay, seteamos la sección y los datos
    else {
      if (!section) {
        this.selectedSection = section;
      }  else {
        const translated = this.translationEnabled()
          ? this.translationData()[section]
          : null;

          if(this.viewId) { //display proper section format for when it is a copied link
            let separatorString = '&nbsp;';

            if((!this.isGaException() && this.seatmapDisplayEnabled())) {
              separatorString = '&nbsp; | &nbsp;';
            }

            // Always remove pipe for INT sections
            if (/\(INT\)(R|E)?/.test(section)) {
              separatorString = '&nbsp;';
            }
            
            this.selectedSection = translated ?? this.dvmFormatter.transform(section, true, false, true, separatorString);
          } else {
            this.selectedSection = translated ?? (this.is3d ? this.formatSectionDataForAmenity(section) : this.dvmFormatter.transform(section));
          }
        }

      this.sectionData = sectionData;

    }
    this.cdr.markForCheck();
  }

  private setSection(section: string, is3d: boolean = false): void {
      const translated = this.translationEnabled() ? this.translationData()[section] : null;

      if(translated) {
        this.selectedSection = this.translationData()[section]
      } else if (!is3d) {
         this.selectedSection = this.dvmFormatter.transform(section);
      } else {
        this.selectedSection = this.formatSectionDataForAmenity(section);        
      }
    this.cdr.detectChanges();
  }

  private toggleNeighbors(show: boolean): void {

    if(this.toggleAnimation) {
      this.toggleAnimation.pause()
      this.toggleAnimation = null;
    }

    if(show && !this.showElement){
      this.showElement = true;
      this.cdr.markForCheck();
    }
    
    this.toggleAnimation = anime({
      targets: this.neighborsContainer.nativeElement,
      translateY: show ? 0 : 50,
      opacity: show ? 1 : 0,
      duration: 300,
      easing: 'easeInOutQuad',
      complete: () => {
        if(!show){
          this.showElement = false;
        }
        this.toggleAnimation = null;
      }
    })


  }
  
  private animateSectionNumbers(section: string, sectionData: NeighborRecord): void {
    
    const targets: HTMLElement[] = [
      this.currentSection?.nativeElement ?? null, 
      this.currentSection3d?.nativeElement ?? null,
      this.leftSectionElement?.nativeElement ?? null, 
      this.rightSectionElement?.nativeElement ?? null
    ].filter(el => el !== null) as HTMLElement[];

    anime({
      targets:  targets,
      duration: 500,
      filter:   ['blur(0px)', 'blur(2px)', 'blur(0px)'],
      easing:   'easeInOutQuad',
      update:   (animation): void => {
  
        if(animation.progress >= 50 && this.selectedSection !== section.split('_')[1]){
          this.setSection(section, this.view() === AppViews.Pano);
          this.sectionData      = sectionData;
        }
        
      }
    })

  }

  private formatSectionDataForAmenity(section: string): string {
    const translationMap = this.translationEnabled() ? this.translationData() : undefined;
    const sectionData = this.dvmFormatter.transform(section, true, false, true, '&nbsp; | &nbsp;', translationMap);

    if (!this.sectionIsPremium) return sectionData;

    return sectionData
      .split('&nbsp; | &nbsp;')
      .map(item => {
        const parts = item.trim().split(' ');
        const label = parts[0];
        const value = parts.slice(1).join(' ');

        return `<span class="text-vip">${label}</span> <span class="text-white">${value}</span>`;
      })
      .join(' <span class="text-vip">&nbsp; | &nbsp;</span> ');
  }


  private initComponent(): void {

    const viewer3d = this.dvmService.viewer3d,
          viewer   = this.dvmService.viewer;

    viewer3d.waitInitialize().subscribe((): void => {
      
      this.subscriptions.push(
        
        viewer.getObservable('load_success').subscribe((): void => this.setCurrentSection(viewer3d.getViewId() ?? undefined)),
        viewer3d.getObservable('load_start').subscribe((): boolean => this.isLoading3d = true),
        viewer3d.getObservable('load_success').subscribe((): boolean => this.isLoading3d = false),
        viewer3d.getObservable('load_error').subscribe((): boolean => this.isLoading3d = false)

      )

    })


  }

  /* 
    When coming from a link with related preview, set it as current preview
    We can't access relatedPreviews as it comes from dvm, so we receive the label from query param 
  */
  private setCurrentPreview(): void {

    if(!this.relatedPreviewLabel) return;
    
    const relatedPreview: Viewer3dRelatedPreviewObject = {
      id: this.viewId!,
      venue_id: this.dvmService.currentVenue!,
      view_id: this.viewId!,
      label: this.relatedPreviewLabel
    };

    this.initialViewId = null;

    queueMicrotask(() => this.relatedPreviewsService.currentPreview.set(relatedPreview));

  }

}
